<template>
  <div class="config-page">
    <PageHeader
      title="Configuration"
      subtitle="Manage lending rate tables and the prime payers catalog."
      :breadcrumbs="[
        { label: 'Financial Assistant', to: { name: 'chatbot' }, icon: 'las la-home' },
        { label: 'Configuration' }
      ]"
    >
      <template #actions>
        <BaseButton
          v-if="activeTab === 'rates' && !loading && !error"
          stylevariant="primary"
          :cus_style="{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
          :disabled="saving"
          :loading="saving"
          @click="saveConfiguration"
        >
          <i class="las la-save"></i>
          Save
        </BaseButton>
        <BaseButton
          stylevariant="secondary"
          :cus_style="{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
          @click="$router.push({ name: 'chatbot' })"
        >
          <i class="las la-arrow-left"></i>
          Back
        </BaseButton>
      </template>
    </PageHeader>

    <div class="config-body" :class="{ 'config-body-full': activeTab === 'prime-payers' }">
      <div class="config-tabs" role="tablist">
        <button
          type="button"
          class="config-tab"
          :class="{ active: activeTab === 'rates' }"
          role="tab"
          :aria-selected="activeTab === 'rates'"
          @click="setActiveTab('rates')"
        >
          Lending Rates
        </button>
        <button
          type="button"
          class="config-tab"
          :class="{ active: activeTab === 'prime-payers' }"
          role="tab"
          :aria-selected="activeTab === 'prime-payers'"
          @click="setActiveTab('prime-payers')"
        >
          Prime Payers
        </button>
      </div>

      <div v-show="activeTab === 'rates'" class="config-tab-panel" role="tabpanel">
        <div v-if="loading" class="loading-state">
          <BaseSpinner />
          <p>Loading configuration...</p>
        </div>

        <div v-else-if="error" class="error-state">
          <p>{{ error }}</p>
          <BaseButton
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.875rem' }"
            @click="loadConfiguration"
          >
            Retry
          </BaseButton>
        </div>

        <template v-else>
          <section
            v-for="table in tables"
            :key="table.name"
            class="rate-section"
          >
            <div class="rate-section-heading">
              <div class="rate-section-heading-text">
                <span>{{ table.name }}</span>
                <span v-if="table.description" class="rate-section-desc">{{ table.description }}</span>
              </div>
              <BaseButton
                :stylevariant="table.is_active ? 'primary' : 'secondary'"
                :cus_style="{ width: 'auto', padding: '0.4rem 0.85rem', fontSize: '0.8rem' }"
                :disabled="table.is_active || saving"
                @click="setActiveTable(table.name)"
              >
                {{ table.is_active ? 'Active' : 'Set Active' }}
              </BaseButton>
            </div>

            <div class="rate-table-wrapper">
              <table class="rate-table">
                <thead>
                  <tr>
                    <th class="col-risk">Lending Rates</th>
                    <th v-for="tenor in tenors" :key="tenor">{{ tenor }} days</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in table.rows"
                    :key="`${table.name}-${row.risk_grade}`"
                  >
                    <td class="col-risk">
                      <span
                        class="risk-rating-badge"
                        :class="getRiskGradeClass(row.risk_grade)"
                      >{{ row.risk_grade }}</span>
                    </td>
                    <td v-for="tenor in tenors" :key="tenor">
                      <div class="rate-input-wrap">
                        <input
                          v-model="row[getTenorField(tenor)]"
                          type="number"
                          step="0.01"
                          min="0"
                          class="rate-input"
                          placeholder="0.00"
                        />
                        <span class="rate-pct">%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
      </div>

      <div v-show="activeTab === 'prime-payers'" class="config-tab-panel" role="tabpanel">
        <PrimePayersCatalog ref="primePayersCatalog" />
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from './components/PageHeader.vue';
import PrimePayersCatalog from './components/PrimePayersCatalog.vue';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';
import ChatbotService from '@/services/chatbot.service';

const TENORS = [30, 60, 90, 45];

const DEFAULT_TABLES = [
  {
    name: 'BAU',
    description: 'Business As Usual lending rates',
    is_active: true,
    rows: [
      { risk_grade: 'Low Risk', '30_days': 2.06, '60_days': 4.16, '90_days': 6.31, '45_days': 3.1, color: '#92D050' },
      { risk_grade: 'Moderate Low Risk', '30_days': 2.14, '60_days': 4.33, '90_days': 6.56, '45_days': 3.23, color: '#FFFF99' },
      { risk_grade: 'Moderate Risk', '30_days': 2.24, '60_days': 4.53, '90_days': 6.87, '45_days': 3.38, color: '#FFC000' },
      { risk_grade: 'High Risk', '30_days': 3.48, '60_days': 7.09, '90_days': 10.82, '45_days': 5.27, color: '#FF0000' }
    ]
  },
  {
    name: 'Stress',
    description: 'Stress lending rates',
    is_active: false,
    rows: [
      { risk_grade: 'Low Risk', '30_days': 2.46, '60_days': 4.98, '90_days': 7.57, '45_days': 3.72, color: '#92D050' },
      { risk_grade: 'Moderate Low Risk', '30_days': 2.54, '60_days': 5.15, '90_days': 7.83, '45_days': 3.84, color: '#FFFF99' },
      { risk_grade: 'Moderate Risk', '30_days': 2.64, '60_days': 5.35, '90_days': 8.13, '45_days': 3.99, color: '#FFC000' },
      { risk_grade: 'High Risk', '30_days': 3.89, '60_days': 7.92, '90_days': 12.11, '45_days': 5.88, color: '#FF0000' }
    ]
  }
];

function formatRateValue(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return numericValue;
}

function parseRow(row) {
  const parsed = {
    risk_grade: row.risk_grade || '',
    color: row.color || '#cbd5e0'
  };

  TENORS.forEach((tenor) => {
    const field = `${tenor}_days`;
    parsed[field] = formatRateValue(row[field]);
  });

  return parsed;
}

function parseTable(table) {
  return {
    name: table.name || '',
    description: table.description || '',
    is_active: !!table.is_active,
    rows: Array.isArray(table.rows) ? table.rows.map(parseRow) : []
  };
}

function parseConfiguration(configuration) {
  const tables = configuration?.tables;

  if (!Array.isArray(tables) || tables.length === 0) {
    return DEFAULT_TABLES.map(parseTable);
  }

  return tables.map(parseTable);
}

function serializeRow(row) {
  const serialized = {
    risk_grade: row.risk_grade || ''
  };

  TENORS.forEach((tenor) => {
    const field = `${tenor}_days`;
    const value = row[field];
    serialized[field] = value === '' || value === null || value === undefined
      ? 0
      : Number(value);
  });

  return serialized;
}

function serializeTable(table) {
  return {
    name: table.name || '',
    description: table.description || '',
    is_active: !!table.is_active,
    rows: Array.isArray(table.rows) ? table.rows.map(serializeRow) : []
  };
}

function buildConfigurationPayload(tables) {
  return {
    tables: tables.map(serializeTable)
  };
}

export default {
  name: 'ConfigPage',
  components: { PageHeader, PrimePayersCatalog, BaseButton, BaseSpinner },
  data() {
    return {
      activeTab: 'rates',
      loading: true,
      saving: false,
      error: null,
      tenors: TENORS,
      tables: DEFAULT_TABLES.map(parseTable)
    };
  },
  async mounted() {
    await this.loadConfiguration();
  },
  methods: {
    setActiveTab(tab) {
      this.activeTab = tab;
      if (tab === 'prime-payers') {
        this.$nextTick(() => {
          this.$refs.primePayersCatalog?.ensureLoaded();
        });
      }
    },

    getTenorField(tenor) {
      return `${tenor}_days`;
    },

    getRiskGradeClass(riskGrade) {
      const normalized = String(riskGrade || '').toLowerCase();

      if (normalized.includes('moderate low') || normalized.includes('moderately low')) {
        return 'risk-rating-moderately-low';
      }
      if (normalized.includes('moderate')) {
        return 'risk-rating-moderate';
      }
      if (normalized.includes('low')) {
        return 'risk-rating-low';
      }
      if (normalized.includes('high')) {
        return 'risk-rating-high';
      }

      return 'risk-rating-unrated';
    },

    setActiveTable(tableName) {
      this.tables = this.tables.map((table) => ({
        ...table,
        is_active: table.name === tableName
      }));
    },

    async loadConfiguration() {
      this.loading = true;
      this.error = null;

      try {
        const configuration = await ChatbotService.getConfiguration();
        this.tables = parseConfiguration(configuration);
      } catch (err) {
        console.error('Error loading configuration:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to load configuration.';
        this.tables = DEFAULT_TABLES.map(parseTable);
      } finally {
        this.loading = false;
      }
    },

    async saveConfiguration() {
      if (this.saving || this.loading) {
        return;
      }

      const invalidField = this.findInvalidRateField();
      if (invalidField) {
        this.$bvToast.toast('Please enter valid rates for all fields before saving.', {
          title: 'Invalid values',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }

      this.saving = true;

      try {
        const payload = buildConfigurationPayload(this.tables);
        const configuration = await ChatbotService.updateConfiguration(payload);

        if (Array.isArray(configuration?.tables) && configuration.tables.length > 0) {
          this.tables = parseConfiguration(configuration);
        }

        this.$bvToast.toast('Configuration saved successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (err) {
        console.error('Error saving configuration:', err);
        this.$bvToast.toast(
          err.response?.data?.message || err.message || 'Failed to save configuration.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.saving = false;
      }
    },

    findInvalidRateField() {
      for (const table of this.tables) {
        for (const row of table.rows || []) {
          for (const tenor of TENORS) {
            const field = `${tenor}_days`;
            const value = row[field];

            if (value === '' || value === null || value === undefined) {
              return field;
            }

            const numericValue = Number(value);
            if (!Number.isFinite(numericValue) || numericValue < 0) {
              return field;
            }
          }
        }
      }

      return null;
    }
  }
};
</script>

<style lang="scss" scoped>
@import './index.scss';

.config-page {
  width: 100%;
  min-height: 100%;
  background: #eef1f6;
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;
}

.config-body {
  padding: 1rem 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 900px;

  &.config-body-full {
    max-width: none;
    width: 100%;
  }
}

.config-tabs {
  display: flex;
  gap: 0.35rem;
  width: fit-content;
}

.config-tab {
  background: transparent;
  border: 1px solid #e2e8f0;
  padding: 0.65rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6c757d;
  font-family: $brand-font-medium;

  &:hover {
    border-color: $color-primary;
    color: $color-primary;
  }

  &.active {
    background: $color-primary;
    color: $color-secondary;
    border-color: $color-primary;
  }
}

.config-tab-panel {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: #718096;
  font-family: $brand-font-regular;

  p {
    margin: 0;
  }
}

.rate-section-heading {
  padding-left: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 1rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  color: #2d3748;
  margin-bottom: 0.75rem;
}

.rate-section-heading-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.rate-section-desc {
  font-size: 0.82rem;
  font-weight: 400;
  font-family: $brand-font-regular;
  color: #a0aec0;
  margin-left: 0.15rem;
}

.rate-table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e6e9f0;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
}

.rate-table {
  width: 100%;
  border-collapse: collapse;

  thead tr {
    background: #f0f4fa;
  }

  th {
    padding: 0.75rem 1.25rem;
    text-align: center;
    font-size: 0.875rem;
    font-weight: 700;
    color: #2d3748;
    font-family: $brand-font-medium;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;

    &.col-risk {
      text-align: left;
      min-width: 210px;
    }
  }

  tbody tr {
    border-bottom: 1px solid #edf2f7;
    transition: background 0.12s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8fafc;
    }
  }

  td {
    padding: 0.65rem 1.25rem;
    text-align: center;
    vertical-align: middle;

    &.col-risk {
      text-align: left;
    }
  }
}

.risk-rating-badge {
  display: inline-block;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  color: #ffffff;
  font-family: $brand-font-semi-bold;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 8px -2px rgba(16, 24, 40, 0.25);

  &.risk-rating-very-low { background: #16a34a; }
  &.risk-rating-low { background: #65a30d; }
  &.risk-rating-moderately-low { background: #ca8a04; }
  &.risk-rating-moderate { background: #d97706; }
  &.risk-rating-high { background: #ea580c; }
  &.risk-rating-unacceptable { background: #ef4444; }
  &.risk-rating-unrated { background: #64748b; }
}

.rate-input-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
}

.rate-input {
  width: 70px;
  padding: 0.35rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: $brand-font-regular;
  color: #2d3748;
  text-align: right;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.15);
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  appearance: textfield;
  -moz-appearance: textfield;
}

.rate-pct {
  font-size: 0.85rem;
  color: #a0aec0;
  font-family: $brand-font-regular;
}

@media (max-width: 640px) {
  .config-body {
    padding: 0.75rem 1rem 0;
  }

  .rate-section-heading,
  .rate-section-heading-text {
    align-items: flex-start;
    flex-direction: column;
  }

  .rate-input {
    width: 56px;
  }
}
</style>
